// Боковая панель объекта (components/Drawer/README.md; ux.md, раздел 4) на Radix Dialog:
// справа, без затемнения — список слева остаётся живым, клик по другой строке меняет
// содержимое. Esc закрывает, фокус возвращается на строку. До 640 px — окно снизу на весь
// экран с удержанием фокуса.
//
// Ширина 620 px и тень `shadow-drawer` — из `.t-drawer` копии «Табеля» от 19.10.2026
// (components/bundle.css). Пакет ширину обычной панели не переопределяет.
//
// «Развернуть» — режим «на весь экран»: панель по центру поверх затемнения, Tab не выходит из
// панели, Esc и «Свернуть» возвращают панель на место (ux.md, «Слои и Esc»: развёрнутая панель —
// слой выше обычной). Режим меняется без пересоздания содержимого: правка в карточке не теряется.
import * as Dialog from '@radix-ui/react-dialog';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx.ts';
import { isToastTarget } from '../lib/layers.ts';
import { useIsPhone } from '../lib/media.ts';
import { useReturnFocus } from '../lib/return-focus.ts';
import { ToastLayerRegion } from '../lib/toast-layer.tsx';
import { IconButton } from './IconButton.tsx';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Название объекта в шапке панели (ФИО, ключ записи); имя панели для программ чтения. */
  title: ReactNode;
  /**
   * Шапка после распорки: бейдж, «Готово», «Скопировать ссылку». Порядок шапки (ux.md, раздел 4):
   * headerActions → «Развернуть» → headerMenu («…») → «Закрыть (Esc)».
   */
  headerActions?: ReactNode;
  /** «…» с действиями объекта — после «Развернуть», перед «Закрыть». */
  headerMenu?: ReactNode;
  /** Кнопка «Развернуть» (на весь экран); до 640 px панель и так на весь экран — кнопки нет. */
  expandable?: boolean;
  /** Полоса состояния с действием сверху тела: «Вход заблокирован до 15:40 · Снять блокировку». */
  status?: ReactNode;
  statusTone?: 'info' | 'warn' | 'bad';
  /** Куда вернуть фокус после закрытия; по умолчанию — элемент, с которого открыли панель. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Esc внутри панели: preventDefault оставляет панель открытой — например, Esc отменяет
   * правку поля (ux.md, «Слои и Esc»: правка — слой выше панели).
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  children?: ReactNode;
  className?: string;
}

/** Что получает фокус по Tab внутри панели (у развёрнутой Tab идёт по кругу). */
const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ');

/** Виден ли элемент; где проверки нет (jsdom), — виден. */
function isVisible(element: HTMLElement): boolean {
  return typeof element.checkVisibility === 'function' ? element.checkVisibility() : true;
}

function tabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (element) => element.tabIndex >= 0 && isVisible(element),
  );
}

export function Drawer({
  open,
  onOpenChange,
  title,
  headerActions,
  headerMenu,
  expandable = true,
  status,
  statusTone = 'warn',
  returnFocusRef,
  onEscapeKeyDown,
  children,
  className,
}: DrawerProps) {
  const isPhone = useIsPhone();
  const contentRef = useRef<HTMLDivElement>(null);
  // Пока панель открыта, клик по другой строке меняет и содержимое, и место возврата фокуса.
  const returnFocus = useReturnFocus(open, contentRef, { returnFocusRef, trackWhileOpen: true });
  // Развёрнута ли панель — в пределах одного открытия: закрытая панель открывается обычной.
  const [mode, setMode] = useState({ open, expanded: false });
  if (mode.open !== open) setMode({ open, expanded: false });
  const expanded = mode.open === open && mode.expanded && open && expandable && !isPhone;
  const setExpanded = (next: boolean) => setMode({ open, expanded: next });

  const keepOpen = (event: Event) => {
    // Без затемнения список остаётся живым: клик и фокус снаружи панель не закрывают.
    // На весь экран (до 640 px) панель не закрывает и нажатие в тосте: «Отменить» в нём.
    // Развёрнутую панель тоже: затемнение само возвращает её на место.
    if (!isPhone || isToastTarget(event)) event.preventDefault();
  };

  const onEscape = (event: KeyboardEvent) => {
    onEscapeKeyDown?.(event);
    if (event.defaultPrevented || !expanded) return;
    // Esc закрывает один верхний слой: сначала развёрнутый режим, затем саму панель.
    event.preventDefault();
    setExpanded(false);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const content = contentRef.current;
    if (!expanded || !content || event.key !== 'Tab' || event.altKey || event.ctrlKey) return;
    // Развёрнутая панель держит Tab внутри (README «Доступность»). С последнего элемента на
    // первый и обратно переводит Radix; здесь — Shift+Tab с самой панели и фокус, ушедший наружу.
    const items = tabbables(content);
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === content || !content.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && !content.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={isPhone}>
      <Dialog.Portal>
        {expanded ? (
          <div
            className="kp-drawer-scrim"
            aria-hidden="true"
            // Нажатие не уводит фокус из панели: затемнение только сворачивает её.
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => setExpanded(false)}
          />
        ) : null}
        <Dialog.Content
          ref={contentRef}
          className={cx('t-drawer kp-drawer', expanded && 'is-expanded', className)}
          aria-describedby={undefined}
          aria-modal={expanded || undefined}
          onOpenAutoFocus={(event) => {
            // Фокус — на саму панель: программа чтения называет её, Tab идёт внутрь.
            event.preventDefault();
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={returnFocus}
          onEscapeKeyDown={onEscape}
          onPointerDownOutside={keepOpen}
          onFocusOutside={keepOpen}
          onInteractOutside={keepOpen}
          onKeyDown={onKeyDown}
        >
          <div className="t-drawer-h">
            <Dialog.Title className="kp-drawer-title">{title}</Dialog.Title>
            <span className="sp" />
            {headerActions}
            {expandable && !isPhone ? (
              <IconButton
                icon={expanded ? Minimize2 : Maximize2}
                label={expanded ? 'Свернуть' : 'Развернуть'}
                size="sm"
                onClick={() => setExpanded(!expanded)}
              />
            ) : null}
            {headerMenu}
            <Dialog.Close asChild>
              <IconButton icon={X} label="Закрыть" shortcut="Esc" size="sm" />
            </Dialog.Close>
          </div>
          <div className="t-drawer-b">
            {status != null ? (
              <div className={cx('kp-drawer-status', statusTone)} role="status">
                {status}
              </div>
            ) : null}
            {children}
          </div>
          <ToastLayerRegion active={open} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
